// Manager Entry — the weekly form, rebuilt, then REDESIGNED to one line.
//
// The live original is a SEPARATE deployed Apps Script web app
// (`~/nvManager-files/workspace/form/Code.gs`), and it is the surface six
// people actually use every week. The first port of it here reproduced its
// shape: nine sections, each with its own list of blank rows, one Submit at
// the bottom for the lot. The owner looked at that on 2026-08-15 and asked for
// something shorter — ONE line: what · who · amount · detail · Submit — with
// the items already filed showing underneath, and the ability to correct or
// delete one after the fact. This is that form.
//
// WHAT CHANGED, AND WHAT DELIBERATELY DID NOT:
//
//   * One line submits on its own. Server-side that is a ONE-ROW BATCH, which
//     is still a batch: same statement, same atomicity, same roster check,
//     same closed-period refusal, same idempotency. Nothing was relaxed to
//     make the line shorter.
//   * DELETE is a soft VOID (026_entry_void.sql) — the row stays, flagged,
//     with who voided it and when. EDIT is "submit the corrected line, then
//     void the old one", in that order: if the new line is refused, the
//     original is still there, which is the failure mode a manager can
//     recover from. The reverse order can lose an entry.
//
// FOUR THINGS ARE STILL LOAD-BEARING rather than cosmetic:
//
//   1. THE SECTIONS COME FROM THE SERVER. `sections` and `rosterSections` are
//      fetched, never hard-coded, because each carries the ADP pay code the
//      payroll engine reads. A second copy of that list in the front end is
//      how one of them eventually gains a code nobody ruled on.
//
//   2. ONE IDEMPOTENCY KEY PER SUBMISSION, reused on every retry of that same
//      line and replaced only after it lands. That is the double-click
//      defence: the server replays the first result instead of paying someone
//      twice. Each void gets its own key, for the same reason.
//
//   3. REFUSALS ARE THE SERVER'S OWN WORDS. "Pay period 2026-P18 is CLOSED.
//      Contact Tomer — do not re-key this into another period." is a sentence
//      a manager can act on; "Something went wrong" is not (INV-9). The
//      per-row `problems` list is rendered as a list, for the same reason.
//
//   4. NO HOURS FIELD EXISTS, anywhere in this file. Standing rule 2: ESTs and
//      LMTs never get clocked hours from a manager, only from Meevo/MES10. The
//      server refuses hours in a body and the database has no column for them;
//      this form not offering one is the third layer, not the only one.
import type { JSX } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { useTableSort } from '../lib/tableSort';
import { SortableTh } from './SortableTh';
import { ManagerEntryReport } from './ManagerEntryReport';
import { rosterDetailRequest } from '../lib/managerEntryReport';
import { setRosterEnteredInAdp } from '../lib/managerEntryReport';
import { session } from '../lib/session';
import { authedGet } from '../lib/apiClient';
import { ApiError } from '../lib/apiClient';
import { useResource, refetchResource } from '../lib/store';
import { LOCATION_NAMES, type LocationCode } from '../lib/locations';
import { payPeriodLabel } from '../lib/payPeriodLabel';
import { formatDate } from '../lib/date';
import { clinicToday } from '../lib/clinicDay';
import { addDaysIso } from '../lib/civilDate';
import { EmptyState, FaultState } from './FaultState';
import {
  loadManagerEntry, submitManagerEntry, voidEntryLine, voidTargetForLine, moveEntryLine,
  correctRosterChange, toOneLinePayload, oneLineIsSendable, blankOneLine, successMessage,
  CANDACE_BONUS, asCandaceBonus,
  suggestedRaiseRate, MANAGER_ENTRY_KEY,
  type OneLineInput, type ManagerEntryData, type EntrySection, type EntryPayPeriod,
  type EntryLine, type RosterSection, type RosterChange, type VoidTarget,
  type PendingRaise, type AdpCheck, type SecureDetails, revealRosterSecure,
} from '../lib/managerEntry';
import { decideHourRaise, decisionCaption } from '../lib/hourRaises';
import {
  fetchRecruitingCandidate, recruitingCandidateKey, type CandidateDetail,
} from '../lib/recruitingApi';

interface RosterPerson {
  id: number;
  full_name: string;
  file_no: string;
  home_location_code: string;
  status: string | null;
}

const ROSTER_KEY = 'manager-entry-roster';

const ROSTER_CHANGE_LABELS: Record<RosterChange['changeType'], string> = {
  NEW_HIRE: 'New hire',
  TERMINATION: 'Termination',
  RATE_CHANGE: 'Rate change',
  DIRECT_DEPOSIT: 'Direct deposit update',
  MISC: 'MISC',
};

/** SSN and direct-deposit boxes — on a new hire, and on the "SSN & direct
 * deposit" line for someone already on the roster (owner, 2026-09-24). */
function RestrictedFields(
  { line, patch, withSsn = true }:
  { line: OneLineInput; patch: (p: Partial<OneLineInput>) => void; withSsn?: boolean },
): JSX.Element {
  return (
    <>
      <div class="section-head">
        <p><strong>{withSsn ? 'SSN & direct deposit' : 'New bank details'}</strong> — shown as the last 4 digits; the owner, director and this clinic's manager can show the full numbers any time</p>
      </div>
      <div class="money-add">
      {withSsn ? (
        <label class="nh-field">
        <span class="nh-label">SSN</span>
        <input
          type="password" inputMode="numeric" maxLength={9} autocomplete="off"
          value={line.ssn} placeholder="SSN" aria-label="SSN"
          onInput={(e: Event) => patch({ ssn: (e.target as HTMLInputElement).value })}
        />
      </label>
      ) : null}
      <label class="nh-field">
        <span class="nh-label">Bank name</span>
        <input
          type="text" autocomplete="off"
          value={line.bankName} placeholder="Bank name" aria-label="Bank name"
          onInput={(e: Event) => patch({ bankName: (e.target as HTMLInputElement).value })}
        />
      </label>
      <label class="nh-field">
        <span class="nh-label">Routing number</span>
        <input
          type="text" inputMode="numeric" maxLength={9} autocomplete="off"
          value={line.routingNumber} placeholder="Routing number" aria-label="Routing number"
          onInput={(e: Event) => patch({ routingNumber: (e.target as HTMLInputElement).value })}
        />
      </label>
      <label class="nh-field">
        <span class="nh-label">Account number</span>
        <input
          type="password" inputMode="numeric" autocomplete="off"
          value={line.accountNumber} placeholder="Account number" aria-label="Account number"
          onInput={(e: Event) => patch({ accountNumber: (e.target as HTMLInputElement).value })}
        />
      </label>
    </div>
    </>
  );
}

/** Owner, 2026-09-24: "it needs to show like last 4 digits with xxx before
 * then reveal shows all" — as many times as anyone likes. Each Show is
 * recorded server-side, silently; nothing limits it. The last-4s before Show
 * were dropped (Cursor review #35: that list read was never logged). */
function ShowSecure(
  { rosterChangeId, tail, withSsn = true }:
  { rosterChangeId: number; tail: NonNullable<RosterChange['secureTail']>; withSsn?: boolean },
): JSX.Element {
  const [shown, setShown] = useState<SecureDetails | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function show(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      setShown(await revealRosterSecure(rosterChangeId));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }
  // Cursor review #35: the list no longer carries last-4s or the bank name
  // (that read was not logged). Until Show — which is logged — every field
  // is a plain mask; `tail` only says details are on file.
  const hidden = tail.onFile ? 'on file' : '—';
  return (
    <div class="secure-shown" data-testid={`secure-${rosterChangeId}`}>
      {withSsn ? <div>SSN: {shown ? (shown.ssn ?? '—') : `xxx-xx-xxxx (${hidden})`}</div> : null}
      <div>Bank: {shown ? (shown.bankName ?? '—') : hidden}</div>
      <div>Routing: {shown ? (shown.routingNumber ?? '—') : 'xxxxxxxxx'}</div>
      <div>Account: {shown ? (shown.accountNumber ?? '—') : 'xxxxxxxx'}</div>
      {shown ? (
        <button type="button" class="btn" onClick={() => setShown(null)} data-testid={`secure-hide-${rosterChangeId}`}>Hide</button>
      ) : (
        <button type="button" class="btn" disabled={busy} onClick={() => void show()} data-testid={`secure-show-${rosterChangeId}`}>
          {busy ? 'Loading…' : 'Show'}
        </button>
      )}
      {error ? <div class="bad">{error}</div> : null}
    </div>
  );
}

/** N-088 (Request Ledger, owner 2026-09-08): the ADP verdict on a
 * manager-entered termination, OWNER only. `.bad` for "act now" (they were
 * not paid — terminate today), `.warn` for "wait" (they were paid — ADP
 * termination has to land on the NEXT cycle, rule 5) and for the honest
 * "cannot tell yet" case, never a guess either way. */
function PayVerdictPill({ verdict }: { verdict: RosterChange['payVerdict'] }): JSX.Element | null {
  if (!verdict) return null;
  const cls = verdict.code === 'NO_PAY_NOW' ? 'bad' : 'warn';
  return (
    <span class={`badge ${cls}`} data-testid={`pay-verdict-${verdict.code}`}>
      {verdict.label}
    </span>
  );
}

/** Owner, 2026-09-23: the ADP confirmation of a termination (import, then
 * register, for the first period after the last day). OWNER only, like the
 * N-088 pill beside it. The short label is on the pill; the full sentence —
 * which week, which file — is on the line under it. */
const ADP_CHECK_LABEL: Record<AdpCheck['code'], string> = {
  CONFIRMED: 'ADP confirmed',
  WAITING_IMPORT: 'waiting on ADP import',
  WAITING_REGISTER: 'waiting on register',
  FLAG: 'STILL PAID after last day',
  ERROR: 'ADP check failed',
};

function AdpCheckPill({ check }: { check: RosterChange['adpCheck'] }): JSX.Element | null {
  if (!check) return null;
  const cls = check.code === 'CONFIRMED' ? 'ok' : check.code === 'FLAG' || check.code === 'ERROR' ? 'bad' : 'warn';
  const label = check.code === 'CONFIRMED' && check.status === 'TERMINATED'
    ? 'ADP confirmed · Terminated'
    : ADP_CHECK_LABEL[check.code];
  return (
    <>
      <span class={`badge ${cls}`} data-testid={`adp-check-${check.code}`}>{label}</span>
      <div class="muted" data-testid="adp-check-text">{check.text}</div>
    </>
  );
}

/** Earliest week that has started and is still open — not the week today
 * falls in. Owner, 2026-09-08: week 37 stays the default until he closes it.
 * Same rule as `raisePayPeriod` in src/hour_raise.js. */
export function currentPeriod(periods: EntryPayPeriod[], today: string): EntryPayPeriod | null {
  if (!periods.length) return null;
  const byStart = [...periods].sort((a, b) => a.startDate.localeCompare(b.startDate));
  const startedOpen = byStart.filter((p) => p.startDate <= today && !p.closed);
  if (startedOpen.length) return startedOpen[0];
  return byStart.find((p) => !p.closed && p.startDate > today)
    ?? byStart.find((p) => p.startDate > today)
    ?? byStart[byStart.length - 1]
    ?? null;
}

/** Today as YYYY-MM-DD at the clinics (Eastern) — not UTC's day, not the
 * viewer's computer (owner, 2026-10-16). */
export function todayLocal(now: Date = new Date()): string {
  return clinicToday(now);
}

/** `clinic` picks this period's own dates out of `datesByCompany` when the
 * period carries one (a caller who sees all four clinics at once) — see
 * payPeriodLabel/datesForClinic in ../lib/payPeriodLabel. */
export function periodLabel(p: EntryPayPeriod, clinic?: LocationCode | null): string {
  return payPeriodLabel(p, clinic);
}

/** Owner, 2026-09-07, superseding L-15 (2026-08-17): a pay period cannot be
 * "ended" for entry the moment its last day passes — payroll is not submitted
 * until days later, and managers are still filing against it. Tomer: "week 37
 * has not been submitted yet, it can never be ended before that... it should
 * be a week after the period end date is when it should close." So entry stays
 * open through the SEVENTH day after the period's end date, on the clinic's own
 * calendar day — the same string `currentPeriod` compares. A future period is
 * open too. A CLOSED period is a different gate (Tomer locked it): the form
 * stays and the server's own refusal is what the manager reads — that refusal
 * is smoke check step 5, and it must remain reachable from the browser. */
export const ENTRY_GRACE_DAYS = 7;

/** `date` (YYYY-MM-DD) plus n days, as the same kind of string — plain
 * calendar maths (lib/civilDate.ts), no timezone can shift it by a day. */
export function addDays(date: string, n: number): string {
  return addDaysIso(date, n);
}

export function entryOpenFor(p: EntryPayPeriod | null, today: string): boolean {
  if (!p) return false;
  return addDays(p.endDate, ENTRY_GRACE_DAYS) >= today;
}

/** What the manager is being asked to type in the two value boxes, for the
 * "what" they picked. One place, so the labels and the payload can never
 * disagree about which field is which. */
export function fieldLabelsFor(
  what: string, sections: EntrySection[], rosterSections: RosterSection[],
): { isRoster: boolean; label1: string; label2: string; type1: string; type2: string } {
  const money = sections.find((s) => s.key === what);
  if (money) {
    return {
      isRoster: false, label1: 'Amount', label2: money.detailLabel,
      type1: 'number', type2: 'text',
    };
  }
  const roster = rosterSections.find((s) => s.key === what);
  if (roster) {
    return {
      isRoster: true, label1: roster.field1Label, label2: roster.field2Label,
      type1: roster.key === 'misc' ? 'text' : roster.key === 'rate_changes' ? 'number' : 'date',
      type2: roster.key === 'misc' || roster.key === 'terminations' ? 'text' : 'number',
    };
  }
  // Nothing picked yet. The boxes are STILL SHOWN, with the generic labels —
  // owner, 2026-08-15: "we also need entry boxes for Amount and Description".
  // A row that grows two fields only after a dropdown changes reads as a
  // broken form, and hiding them taught nobody what the line was for.
  return {
    isRoster: false, label1: 'Amount', label2: 'Description',
    type1: 'number', type2: 'text',
  };
}

/** Hire → pre-filled New hire (docs/RECRUITING-PLAN.md, "Hire" and the
 * Onboarding bridge). The id in `?candidate=<id>` on THIS page's own URL —
 * read directly from `window.location`, the same way People.tsx reads its
 * `#person-<id>` hash, because the router (lib/router.ts) tracks only the
 * path and drops the query string on purpose (INV-1: a route change alone
 * must never imply a fetch). Anything that is not a bare positive integer is
 * not a candidate id. */
export function candidateIdFromSearch(search: string): number | null {
  const raw = new URLSearchParams(search).get('candidate');
  return raw && /^\d+$/.test(raw) ? Number(raw) : null;
}

/** The candidate's `position` (migration 083: LMT / EST / FRONT_DESK /
 * MANAGER, or null) onto this form's New-hire "Position" box — which is
 * NEW_HIRE_EXTRA_FIELDS' plain free-text field (src/manager_entry.js has no
 * enum for it; a manager can type anything there). Only these four values
 * are ever mapped; anything else — including no position at all — is left
 * for the manager to type, never guessed. */
const CANDIDATE_POSITION_TO_NEW_HIRE: Record<string, string> = {
  LMT: 'LMT',
  EST: 'EST',
  FRONT_DESK: 'Front Desk',
  MANAGER: 'Manager',
};

/** Exactly the extra keys a candidate prefill can set — so "Clear" can undo
 * precisely that and nothing the manager typed afterward (Date of birth,
 * W4 status are never touched by the prefill). */
const CANDIDATE_PREFILL_EXTRA_KEYS = ['position', 'streetAddress', 'city', 'state', 'zipCode', 'phone', 'email'] as const;

export interface CandidatePrefill {
  patch: Partial<OneLineInput>;
  /** "<First Last>" for the "From recruiting: …" line. */
  name: string;
  /** True when `candidate.position` was set but did not match one of the
   * four known values — the Position box is left blank rather than guessed. */
  positionUnmapped: boolean;
}

/**
 * What a hired candidate hands to a fresh New hire line. Hire date, pay
 * rate, date of birth, W4 status and the secure fields (SSN, bank) are
 * never here — those stay blank for the manager to type, same as an
 * ordinary new hire (RECRUITING-PLAN.md: "the candidate becomes a
 * pre-filled New hire"). `email` only — never `emailRelay` (the Indeed
 * relay address, dead the moment the posting closes; rulings 2026-09-25).
 */
export function candidateToNewHireLine(candidate: CandidateDetail['candidate']): CandidatePrefill {
  const extra: Record<string, string> = {};
  if (candidate.address) extra.streetAddress = candidate.address;
  if (candidate.city) extra.city = candidate.city;
  if (candidate.state) extra.state = candidate.state;
  if (candidate.zip) extra.zipCode = candidate.zip;
  if (candidate.phone) extra.phone = candidate.phone;
  if (candidate.email) extra.email = candidate.email;
  const mappedPosition = candidate.position ? CANDIDATE_POSITION_TO_NEW_HIRE[candidate.position] : undefined;
  if (mappedPosition) extra.position = mappedPosition;
  return {
    patch: {
      firstName: candidate.firstName ?? '',
      middleName: candidate.middleName ?? '',
      lastName: candidate.lastName ?? '',
      extra,
    },
    name: [candidate.firstName, candidate.lastName].filter(Boolean).join(' '),
    positionUnmapped: Boolean(candidate.position) && !mappedPosition,
  };
}

export function ManagerEntry({ clinic }: { clinic: LocationCode }): JSX.Element {
  const [periodId, setPeriodId] = useState<number | null>(null);
  const [line, setLine] = useState<OneLineInput>(() => blankOneLine());
  const [batchKey, setBatchKey] = useState<string>(() => crypto.randomUUID());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [problems, setProblems] = useState<string[]>([]);
  const [done, setDone] = useState<string | null>(null);
  // The item this line is REPLACING, if the manager pressed Edit. Held as the
  // void coordinates only — the new line is submitted first and this is voided
  // after it lands.
  const [replacing, setReplacing] = useState<{ target: VoidTarget; id: number } | null>(null);
  const [raiseRates, setRaiseRates] = useState<Record<number, string>>({});
  const [raiseBusy, setRaiseBusy] = useState<number | null>(null);
  // Reject / Move (owner, 2026-09-08) — OWNER only, see isOwner below.
  const [raiseAction, setRaiseAction] = useState<Record<number, 'reject' | 'move' | null>>({});
  const [raiseReason, setRaiseReason] = useState<Record<number, string>>({});
  const [raiseMoveTo, setRaiseMoveTo] = useState<Record<number, number | ''>>({});
  const [raiseDecisionError, setRaiseDecisionError] = useState<Record<number, string>>({});

  // Hire → pre-filled New hire. Read once, at mount — a manager editing the
  // line afterward must never have it silently rewritten from under them.
  const [candidateId] = useState<number | null>(() => candidateIdFromSearch(window.location.search));
  const [candidatePrefill, setCandidatePrefill] = useState<{ id: number; name: string; positionUnmapped: boolean } | null>(null);
  const [candidateCleared, setCandidateCleared] = useState(false);
  const candidateResource = useResource<CandidateDetail>(
    candidateId !== null ? recruitingCandidateKey(candidateId) : null,
    () => fetchRecruitingCandidate(candidateId as number),
  );

  const data = useResource<ManagerEntryData>(MANAGER_ENTRY_KEY, () => loadManagerEntry(null));
  const roster = useResource<{ people: RosterPerson[] }>(ROSTER_KEY, () => authedGet('/api/people'));

  // `?? []` on each, and it is not belt-and-braces. `status === 'ready'` says the
  // REQUEST finished, not that the body carried every field — so a ready payload
  // missing one of these made `periods.find(...)` below throw
  // "Cannot read properties of undefined (reading 'find')" from inside a hook,
  // which unmounts the whole Payroll page rather than degrading.
  //
  // This is the flake HANDOFF-030 recorded as "intermittently red,
  // ManagerEntry.tsx:148, predates tonight" and left alone. It was never a test
  // problem: it is this component trusting the shape of a response it does not
  // control, and the test suite was the only thing reporting it. An empty list
  // renders an empty section, which is the honest outcome.
  const periods = data.status === 'ready' ? data.data.payPeriods ?? [] : [];
  const sections = data.status === 'ready' ? data.data.sections ?? [] : [];
  const rosterSections = data.status === 'ready' ? data.data.rosterSections ?? [] : [];
  const pendingRaises = data.status === 'ready' ? data.data.pendingRaises ?? [] : [];
  const followingPeriod = data.status === 'ready' ? data.data.followingPeriod ?? null : null;
  // Owner, 2026-09-07, said twice: a period Tomer has CLOSED is done and does
  // not appear in this dropdown, full stop. No exception for one already
  // selected — week 35 was closed and still listed because it was the current
  // selection, and that is precisely what he did not want. If the selection
  // falls away the picker lands on the earliest started open week.
  const choosablePeriods = useMemo(() => periods.filter((p) => !p.closed), [periods]);
  const activePeriod = useMemo(
    () => choosablePeriods.find((p) => p.id === periodId)
      ?? currentPeriod(choosablePeriods, todayLocal()),
    [choosablePeriods, periodId],
  );
  const entryOpen = entryOpenFor(activePeriod, todayLocal());

  // Never null: with nothing picked it returns the generic Amount /
  // Description pair, so the row always has all of its boxes.
  const shape = fieldLabelsFor(line.what, sections, rosterSections);
  const isRoster = shape.isRoster;
  const isNewHire = line.what === 'new_hires';
  const newHireSection = rosterSections.find((s) => s.key === 'new_hires');

  // The clinic's own roster, active people only, by name. A manager picking
  // from a list of 115 across four clinics would be picking the wrong person
  // eventually; the server refuses a foreign employee outright, and this keeps
  // the form from offering one in the first place.
  const people = useMemo(() => {
    // Same reasoning as payPeriods above: "ready" is about the request, not the
    // body. Without `?? []` a roster response with no `people` key threw
    // "Cannot read properties of undefined (reading 'filter')" out of a hook.
    const all = roster.status === 'ready' ? roster.data.people ?? [] : [];
    return all
      .filter((p) => p.home_location_code === clinic && p.status !== 'TERMINATED')
      .sort((a, b) => a.full_name.localeCompare(b.full_name));
  }, [roster, clinic]);

  function patch(next: Partial<OneLineInput>): void {
    setLine((prev) => ({ ...prev, ...next }));
  }

  // Apply the candidate prefill exactly once, and only onto a line the
  // manager has not already started (`line.what === ''`) — a slow fetch must
  // never overwrite something they were mid-typing. `candidatePrefill` being
  // set is what stops this from reapplying itself once it has run.
  useEffect(() => {
    if (candidateId === null || candidateCleared || candidatePrefill !== null) return;
    if (candidateResource.status !== 'ready') return;
    if (line.what !== '') return;
    const { patch: candidatePatch, name, positionUnmapped } = candidateToNewHireLine(candidateResource.data.candidate);
    setLine({ ...blankOneLine('new_hires'), ...candidatePatch, candidateId });
    setCandidatePrefill({ id: candidateId, name, positionUnmapped });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateId, candidateResource, candidateCleared, candidatePrefill, line.what]);

  /** The "Clear" chip on the recruiting banner: removes exactly what the
   * prefill added — name and the extra keys it set — never Date of birth,
   * W4 status or anything else the manager may have typed since. Leaves
   * `what` as 'new_hires' so the New hire block stays open for them to type
   * into by hand. */
  function clearCandidatePrefill(): void {
    setCandidateCleared(true);
    setLine((prev) => {
      const extra = { ...prev.extra };
      for (const k of CANDIDATE_PREFILL_EXTRA_KEYS) delete extra[k];
      return { ...prev, firstName: '', middleName: '', lastName: '', extra, candidateId: null };
    });
  }

  /** Changing WHAT keeps the person and anything already typed — the common
   * case is "same person, wrong category", and re-typing an amount because a
   * dropdown moved is the kind of small friction that sends people back to
   * the spreadsheet. Only the fields that belong to the OTHER kind are
   * cleared, since a hire date is not an amount. */
  function changeWhat(what: string): void {
    if (what === CANDACE_BONUS) {
      setLine((prev) => ({
        ...blankOneLine(what), employeeId: candaceId ?? '', private: true,
        amount: prev.amount, detail: prev.detail, note: prev.note,
      }));
      return;
    }
    setLine((prev) => ({
      ...blankOneLine(what),
      employeeId: what === 'new_hires' || prev.what === CANDACE_BONUS ? '' : prev.employeeId,
      firstName: what === 'new_hires' ? prev.firstName : '',
      middleName: what === 'new_hires' ? prev.middleName : '',
      lastName: what === 'new_hires' ? prev.lastName : '',
      amount: prev.amount,
      detail: prev.detail,
      note: prev.note,
      field1: prev.field1,
      field2: prev.field2,
    }));
    setReplacing(null);
    // A deliberate "what" change — including picking New hire again from a
    // blank slate — starts a line with no connection to recruiting, however
    // this one got here.
    setCandidatePrefill(null);
  }

  function clearLine(): void {
    setLine(blankOneLine());
    setReplacing(null);
    setCandidatePrefill(null);
    // A new line is a NEW key. Reusing the old one would make the next
    // genuinely-different submission look like a retry of this one.
    // Secure fields (SSN, bank, routing, account) live only in this state —
    // replacing it is the only place they need to be wiped, and they never
    // touched localStorage/IndexedDB to begin with.
    setBatchKey(crypto.randomUUID());
  }

  const sendable = oneLineIsSendable(line, isRoster);
  const clinicRaises = pendingRaises.filter((r) => r.locationCode === clinic);
  // Raises are the owner's alone, signed in as himself — never a manager, and
  // not while he views as a clinic (that view is the manager's) (owner, 2026-10-12).
  const ownerHimself = session.value.me?.role === 'OWNER' && !session.value.me?.viewingAs;
  const showRaises = Boolean(
    ownerHimself && entryOpen && followingPeriod && activePeriod?.id === followingPeriod.id && clinicRaises.length,
  );

  async function submitPendingRaise(row: PendingRaise): Promise<void> {
    if (busy || raiseBusy != null || !activePeriod) return;
    const newRate = (raiseRates[row.employeeId] ?? suggestedRaiseRate(row.currentRate)).trim();
    if (!newRate || Number(newRate) <= 0) {
      setError('Type the new pay rate.');
      return;
    }
    setRaiseBusy(row.employeeId);
    setError(null);
    setProblems([]);
    setDone(null);
    try {
      const result = await submitManagerEntry(
        {
          locationCode: clinic,
          payPeriodId: activePeriod.id,
          entries: {
            rate_changes: [{
              employeeId: row.employeeId,
              field1: row.currentRate ?? '',
              field2: newRate,
              note: `1,000-hour raise (${row.serviceHours} hrs)`,
            }],
          },
        },
        crypto.randomUUID(),
      );
      setDone(result.duplicate
        ? (result.message ?? 'Already received — nothing was written twice.')
        : successMessage(
            result.linesWritten ?? result.submission.lineCount,
            result.rosterWritten ?? result.submission.rosterCount ?? 0,
          ));
      if (result.rateWarning) setError(result.rateWarning);
      await refetchResource(MANAGER_ENTRY_KEY, () => loadManagerEntry(null));
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        const { problems: listed } = err.body as { problems?: string[] };
        if (Array.isArray(listed)) setProblems(listed);
      } else {
        setError(String(err));
      }
    } finally {
      setRaiseBusy(null);
    }
  }

  // Reject / Move — OWNER only (owner, 2026-09-08: "Tomer decides raises").
  // Tomer's own raise entries came through THIS page (Submit raise, above),
  // so the decision controls live right next to it rather than only on the
  // Home dashboard's read-only alert.
  const isOwner = session.value.me?.role === 'OWNER';
  // Private bonus lines (097): the owner's and the director's only.
  // Not while viewing as a clinic: that view is exactly the manager's.
  const canSeePrivate = (isOwner || session.value.me?.role === 'DIRECTOR') && !session.value.me?.viewingAs;
  // "Candace bonus" (owner, 2026-10-06): owner and director only; Candace is
  // not on the People list, so the server names her id per clinic.
  const candaceId = canSeePrivate && data.status === 'ready' ? data.data.candace?.[clinic] : undefined;

  function openRaiseAction(employeeId: number, kind: 'reject' | 'move'): void {
    setRaiseAction((prev) => ({ ...prev, [employeeId]: kind }));
    setRaiseDecisionError((prev) => ({ ...prev, [employeeId]: '' }));
  }

  function cancelRaiseAction(employeeId: number): void {
    setRaiseAction((prev) => ({ ...prev, [employeeId]: null }));
  }

  async function confirmRejectRaise(row: PendingRaise): Promise<void> {
    if (!followingPeriod || raiseBusy != null) return;
    const text = (raiseReason[row.employeeId] ?? '').trim();
    if (!text) {
      setRaiseDecisionError((prev) => ({ ...prev, [row.employeeId]: 'A reason is required to reject a raise.' }));
      return;
    }
    setRaiseBusy(row.employeeId);
    try {
      await decideHourRaise({
        employeeId: row.employeeId, payPeriodId: followingPeriod.id, decision: 'REJECTED', reason: text,
      });
      setRaiseAction((prev) => ({ ...prev, [row.employeeId]: null }));
      await refetchResource(MANAGER_ENTRY_KEY, () => loadManagerEntry(null));
    } catch (err) {
      setRaiseDecisionError((prev) => ({
        ...prev,
        [row.employeeId]: err instanceof ApiError ? err.message : String(err),
      }));
    } finally {
      setRaiseBusy(null);
    }
  }

  async function confirmMoveRaise(row: PendingRaise): Promise<void> {
    if (!followingPeriod || raiseBusy != null) return;
    const target = raiseMoveTo[row.employeeId];
    if (target === '' || target === undefined) {
      setRaiseDecisionError((prev) => ({ ...prev, [row.employeeId]: 'Pick the pay period to move this raise to.' }));
      return;
    }
    setRaiseBusy(row.employeeId);
    try {
      await decideHourRaise({
        employeeId: row.employeeId,
        payPeriodId: followingPeriod.id,
        decision: 'DEFERRED',
        deferToPeriodId: Number(target),
        reason: (raiseReason[row.employeeId] ?? '').trim(),
      });
      setRaiseAction((prev) => ({ ...prev, [row.employeeId]: null }));
      await refetchResource(MANAGER_ENTRY_KEY, () => loadManagerEntry(null));
    } catch (err) {
      setRaiseDecisionError((prev) => ({
        ...prev,
        [row.employeeId]: err instanceof ApiError ? err.message : String(err),
      }));
    } finally {
      setRaiseBusy(null);
    }
  }

  // Later OPEN periods to move a raise to — already on hand from `periods`
  // (this page's own payPeriods list), no extra fetch needed.
  const laterOpenPeriods = followingPeriod
    ? periods.filter((p) => !p.closed && p.startDate > followingPeriod.startDate)
    : [];

  async function submit(): Promise<void> {
    if (busy || !activePeriod || !sendable) return;
    setBusy(true);
    setError(null);
    setProblems([]);
    setDone(null);
    try {
      if (replacing && replacing.target === 'roster' && line.what === 'rate_changes') {
        await correctRosterChange(replacing.id, {
          field1: String(line.field1 ?? '').trim(),
          field2: String(line.field2 ?? '').trim(),
          note: String(line.note ?? '').trim(),
        }, batchKey);
        setDone('Raise updated.');
        clearLine();
        await refetchResource(MANAGER_ENTRY_KEY, () => loadManagerEntry(null));
        return;
      }
      const entries = toOneLinePayload(asCandaceBonus(line), isRoster);
      const result = await submitManagerEntry(
        { locationCode: clinic, payPeriodId: activePeriod.id, entries },
        batchKey,
      );
      let message = result.duplicate
        ? (result.message ?? 'Already received — nothing was written twice.')
        : successMessage(
            result.linesWritten ?? result.submission.lineCount,
            result.rosterWritten ?? result.submission.rosterCount ?? 0,
          );
      if (result.rateWarning) message += ` ${result.rateWarning}`;
      // The EDIT half, and only after the replacement landed. A void that
      // fails here leaves BOTH lines visible, which the manager can see and
      // fix; voiding first and then failing to submit loses the entry.
      if (replacing) {
        try {
          await voidEntryLine(replacing.target, replacing.id, crypto.randomUUID());
          message += ' The item it replaces was voided.';
        } catch (err) {
          message += ` NOTE: the replacement was saved but the old item could NOT be voided — ${
            err instanceof ApiError ? err.message : String(err)
          } Delete it below.`;
        }
      }
      setDone(message);
      clearLine();
      await refetchResource(MANAGER_ENTRY_KEY, () => loadManagerEntry(null));
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        const { problems: listed } = err.body as { problems?: string[] };
        if (Array.isArray(listed)) setProblems(listed);
      } else {
        setError(String(err));
      }
    } finally {
      setBusy(false);
    }
  }

  async function voidItem(target: VoidTarget, id: number): Promise<void> {
    if (busy) return;
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      await voidEntryLine(target, id, crypto.randomUUID());
      setDone('Item deleted.');
      await refetchResource(MANAGER_ENTRY_KEY, () => loadManagerEntry(null));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function moveItem(target: VoidTarget, id: number, targetPayPeriodId: number): Promise<void> {
    if (busy || target === 'roster') return;
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      await moveEntryLine(target, id, targetPayPeriodId, crypto.randomUUID());
      setDone('Item moved to the other week.');
      await refetchResource(MANAGER_ENTRY_KEY, () => loadManagerEntry(null));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  /** Edit = load the item into the one line and remember to void it once the
   * replacement lands. Money lines and rate changes: a NEW_HIRE's secure
   * fields are NOT readable (SPEC-001 §7), so those stay Delete-only. */
  function editLine(l: EntryLine): void {
    const section = sections.find((s) => s.payCode === l.payCode);
    if (!section) return;
    const isCandace = candaceId !== undefined && l.employeeId === candaceId && section.key === 'bonus';
    setLine({
      ...blankOneLine(isCandace ? CANDACE_BONUS : section.key),
      employeeId: l.employeeId,
      amount: l.amount,
      detail: l.reason ?? '',
      note: l.note ?? '',
      // A correction of a private bonus stays private (097).
      private: l.private === true,
    });
    setReplacing({ target: voidTargetForLine(l), id: l.id });
    setDone(null);
    setError(null);
    setBatchKey(crypto.randomUUID());
  }

  function editRoster(r: RosterChange): void {
    if (r.changeType !== 'RATE_CHANGE') {
      // N-071 (owner, 2026-10-11): a termination or a MISC note is edited the
      // way a money line is — the corrected line is submitted, then the old
      // one is voided (submit() below). Never a secure row: a new hire's or a
      // direct deposit's SSN / bank fields are not readable (SPEC-001 §7).
      const section = rosterSections.find((s) => s.changeType === r.changeType);
      if (!section || section.secure || !rosterEditable(r)) return;
      setLine({
        ...blankOneLine(section.key),
        employeeId: r.employeeId ?? '',
        field1: String(r.field1 ?? ''),
        field2: String(r.field2 ?? ''),
        note: String(r.note ?? ''),
      });
      setReplacing({ target: 'roster', id: r.id });
      setDone(null);
      setError(null);
      setBatchKey(crypto.randomUUID());
      return;
    }
    setLine({
      ...blankOneLine('rate_changes'),
      employeeId: r.employeeId ?? '',
      field1: String(r.field1 ?? ''),
      field2: String(r.field2 ?? ''),
      note: String(r.note ?? ''),
    });
    setReplacing({ target: 'roster', id: r.id });
    setDone(null);
    setError(null);
    setBatchKey(crypto.randomUUID());
  }

  if (data.status === 'loading') {
    return <EmptyState label="Loading the entry form…" />;
  }
  if (data.status === 'fault') {
    return <FaultState name="the entry form did not load" detail={data.error} />;
  }
  if (!periods.length) {
    // A named gap, not an empty form that silently submits nowhere.
    return (
      <EmptyState label="No pay period is set up yet — nothing can be entered until one exists." />
    );
  }

  return (
    <>
    <section class="card" data-testid="manager-entry">
      <div class="section-head">
        <h2>Manager entry — {LOCATION_NAMES[clinic] ?? clinic}</h2>
        <p>
          One line at a time: pick what it is, who it is for, and the amount.
          Hours are never entered here — they come from Meevo.
        </p>
      </div>

      <div class="money-add">
        <label>
          Pay period:{' '}
          <select
            value={activePeriod?.id ?? ''}
            aria-label="Pay period"
            onChange={(e: Event) => setPeriodId(Number((e.target as HTMLSelectElement).value))}
          >
            {choosablePeriods.map((p) => (
              <option key={p.id} value={p.id}>{periodLabel(p, clinic)}</option>
            ))}
          </select>
        </label>
        {activePeriod?.closed ? (
          <small class="bad" role="alert" data-testid="period-closed">
            This period is closed — the server will refuse a submission against it.
          </small>
        ) : activePeriod && !entryOpen ? (
          <small role="status" data-testid="period-past">
            This pay period closed to entry a week after it ended — below is the record of what was filed. New lines go in the current or a future week.
          </small>
        ) : null}
      </div>

      {roster.status === 'fault' ? (
        <FaultState name="the roster did not load" detail={roster.error} />
      ) : null}

      {showRaises ? (
        <div class="hour-raises" data-testid="hour-raises">
          <h3>Hour raises{followingPeriod ? ` — ${periodLabel(followingPeriod, clinic)}` : ''}</h3>
          <p>
            These people hit 1,000 hours. Enter the new rate. It starts on{' '}
            {followingPeriod ? periodLabel(followingPeriod, clinic) : 'this pay period'}
            {' '}and their hours reset then.
          </p>
          {clinicRaises.map((r) => {
            const caption = decisionCaption(r.decision, followingPeriod, clinic);
            const action = raiseAction[r.employeeId] ?? null;
            return (
            <div class="hour-raise-row" key={r.employeeId}>
              <strong>{r.employeeName}</strong>
              <span>{r.serviceHours} hrs</span>
              <span>Now {r.currentRate ?? '—'}</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={raiseRates[r.employeeId] ?? suggestedRaiseRate(r.currentRate)}
                aria-label={`New rate for ${r.employeeName}`}
                placeholder="New rate"
                onInput={(e: Event) => {
                  const v = (e.target as HTMLInputElement).value;
                  setRaiseRates((prev) => ({ ...prev, [r.employeeId]: v }));
                }}
              />
              <button
                type="button"
                class="btn"
                disabled={busy || raiseBusy != null}
                onClick={() => { void submitPendingRaise(r); }}
              >
                {raiseBusy === r.employeeId ? 'Saving…' : 'Submit raise'}
              </button>
              {isOwner ? (
                action === null ? (
                  <div class="raise-alert-actions" data-testid={`raise-actions-${r.employeeId}`}>
                    <button type="button" class="btn" onClick={() => openRaiseAction(r.employeeId, 'reject')}>Reject</button>
                    <button type="button" class="btn" onClick={() => openRaiseAction(r.employeeId, 'move')}>Move</button>
                  </div>
                ) : action === 'reject' ? (
                  <div class="raise-decision-form">
                    <input
                      type="text"
                      aria-label={`Reason to reject ${r.employeeName}'s raise`}
                      placeholder="Reason (required)"
                      value={raiseReason[r.employeeId] ?? ''}
                      onInput={(e: Event) => {
                        const v = (e.target as HTMLInputElement).value;
                        setRaiseReason((prev) => ({ ...prev, [r.employeeId]: v }));
                      }}
                    />
                    <button
                      type="button"
                      class="btn primary"
                      disabled={raiseBusy === r.employeeId}
                      onClick={() => { void confirmRejectRaise(r); }}
                    >
                      {raiseBusy === r.employeeId ? 'Saving…' : 'Confirm reject'}
                    </button>
                    <button type="button" class="btn" onClick={() => cancelRaiseAction(r.employeeId)}>Cancel</button>
                    {raiseDecisionError[r.employeeId] ? <p class="fault">{raiseDecisionError[r.employeeId]}</p> : null}
                  </div>
                ) : (
                  <div class="raise-decision-form">
                    <select
                      aria-label={`Move ${r.employeeName}'s raise to`}
                      value={raiseMoveTo[r.employeeId] === undefined ? '' : String(raiseMoveTo[r.employeeId])}
                      onChange={(e: Event) => {
                        const v = (e.target as HTMLSelectElement).value;
                        setRaiseMoveTo((prev) => ({ ...prev, [r.employeeId]: v === '' ? '' : Number(v) }));
                      }}
                    >
                      <option value="">Move to…</option>
                      {laterOpenPeriods.map((p) => (
                        <option key={p.id} value={p.id}>{periodLabel(p, clinic)}</option>
                      ))}
                    </select>
                    <input
                      type="text"
                      aria-label={`Reason to move ${r.employeeName}'s raise (optional)`}
                      placeholder="Reason (optional)"
                      value={raiseReason[r.employeeId] ?? ''}
                      onInput={(e: Event) => {
                        const v = (e.target as HTMLInputElement).value;
                        setRaiseReason((prev) => ({ ...prev, [r.employeeId]: v }));
                      }}
                    />
                    <button
                      type="button"
                      class="btn primary"
                      disabled={raiseBusy === r.employeeId}
                      onClick={() => { void confirmMoveRaise(r); }}
                    >
                      {raiseBusy === r.employeeId ? 'Saving…' : 'Confirm move'}
                    </button>
                    <button type="button" class="btn" onClick={() => cancelRaiseAction(r.employeeId)}>Cancel</button>
                    {raiseDecisionError[r.employeeId] ? <p class="fault">{raiseDecisionError[r.employeeId]}</p> : null}
                  </div>
                )
              ) : null}
              {caption ? <span class="muted" data-testid={`raise-decision-${r.employeeId}`}>{caption}</span> : null}
            </div>
            );
          })}
        </div>
      ) : null}

      {entryOpen ? (
        <>
        {candidateId !== null && candidateResource.status === 'fault' ? (
          <FaultState name="the recruiting candidate did not load" detail={candidateResource.error} />
        ) : null}
        {candidatePrefill && !candidateCleared ? (
          <div class="muted" data-testid="candidate-prefill-banner">
            {`From recruiting: ${candidatePrefill.name}`}
            {candidatePrefill.positionUnmapped
              ? ' — position not recognized there, set it by hand'
              : ''}
            {' '}
            <button
              type="button" class="btn"
              onClick={clearCandidatePrefill}
              data-testid="candidate-prefill-clear"
            >
              Clear
            </button>
          </div>
        ) : null}
        {/* THE ONE LINE. Both lists come from the server (see this file's head). */}
        <div class="money-add" data-testid="one-line">
          <label class="nh-field">
            <span class="nh-label">What</span>
            <select
              value={line.what}
              aria-label="What is this"
              onChange={(e: Event) => changeWhat((e.target as HTMLSelectElement).value)}
            >
              <option value="">What…</option>
              {sections.map((s) => (
                <option key={s.key} value={s.key}>{s.title}</option>
              ))}
              {candaceId !== undefined ? (
                <option value={CANDACE_BONUS} data-testid="what-candace-bonus">Candace bonus</option>
              ) : null}
              {rosterSections.map((s) => (
                <option key={s.key} value={s.key}>{s.title}</option>
              ))}
            </select>
          </label>

          {line.what === CANDACE_BONUS ? (
            <label class="nh-field">
              <span class="nh-label">Who</span>
              <span data-testid="who-candace">Candace Tomey</span>
            </label>
          ) : isNewHire ? null : (
            <label class="nh-field">
            <span class="nh-label">Who</span>
            <select
              value={line.employeeId === '' ? '' : String(line.employeeId)}
              aria-label="Who"
              onChange={(e: Event) => {
                const v = (e.target as HTMLSelectElement).value;
                const employeeId = v === '' ? '' : Number(v);
                const due = typeof employeeId === 'number'
                  ? pendingRaises.find((r) => r.employeeId === employeeId)
                  : undefined;
                patch(
                  line.what === 'rate_changes' && due && !replacing
                    ? {
                      employeeId,
                      field1: due.currentRate ?? '',
                      field2: suggestedRaiseRate(due.currentRate),
                    }
                    : { employeeId },
                );
              }}
            >
              <option value="">Who…</option>
              {people.map((p) => (
                <option key={p.id} value={p.id}>{p.full_name}</option>
              ))}
            </select>
            </label>
          )}

          {isNewHire ? null : (
            <>
              {/* Visible labels, not just grey placeholder text — a date box
                  shows no placeholder at all, which is how a manager typed
                  the day of entry as a termination's last day (owner,
                  2026-09-24). */}
              {shape.label1 ? (<label class="nh-field">
                <span class="nh-label">{shape.label1}</span>
                <input
                  type={shape.type1}
                  step={shape.type1 === 'number' ? '0.01' : undefined}
                  min={shape.type1 === 'number' ? '0' : undefined}
                  value={isRoster ? line.field1 : line.amount}
                  placeholder={shape.label1}
                  aria-label={shape.label1}
                  onInput={(e: Event) => {
                    const v = (e.target as HTMLInputElement).value;
                    patch(isRoster ? { field1: v } : { amount: v });
                  }}
                />
              </label>) : null}
              {shape.label2 ? (
                <label class="nh-field">
                  <span class="nh-label">{shape.label2}</span>
                  <input
                    type={shape.type2}
                    step={shape.type2 === 'number' ? '0.01' : undefined}
                    value={isRoster ? line.field2 : line.detail}
                    placeholder={shape.label2}
                    aria-label={shape.label2}
                    onInput={(e: Event) => {
                      const v = (e.target as HTMLInputElement).value;
                      patch(isRoster ? { field2: v } : { detail: v });
                    }}
                  />
                </label>
              ) : null}
              <label class="nh-field">
                <span class="nh-label">Note</span>
                <input
                  type="text" value={line.note} placeholder="Note (optional)"
                  aria-label="Note"
                  onInput={(e: Event) => patch({ note: (e.target as HTMLInputElement).value })}
                />
              </label>
              {/* Private bonus (owner, 2026-10-06; 097): owner and director
                  only — a manager never sees this box, and the server refuses
                  the flag from anyone else. */}
              {line.what === CANDACE_BONUS ? (
                <label class="nh-field">
                  <span class="nh-label" data-testid="candace-private">Private — only Tomer &amp; Candace see it (always)</span>
                </label>
              ) : null}
              {canSeePrivate && line.what === 'bonus' ? (
                <label class="nh-field">
                  <span class="nh-label">
                    <input
                      type="checkbox"
                      checked={line.private === true}
                      aria-label="Private — only Tomer & Candace see it"
                      data-testid="private-bonus"
                      onChange={(e: Event) => patch({ private: (e.target as HTMLInputElement).checked })}
                    />
                    {' Private — only Tomer & Candace see it'}
                  </span>
                </label>
              ) : null}
              <button
                type="button"
                class="btn primary"
                disabled={busy || !sendable || !activePeriod}
                onClick={() => void submit()}
                data-testid="manager-entry-submit"
              >
                {busy ? 'Saving…' : replacing ? 'Save correction' : 'Submit'}
              </button>
              {replacing ? (
                <button type="button" class="btn" onClick={clearLine} data-testid="cancel-edit">
                  Cancel
                </button>
              ) : null}
            </>
          )}
        </div>

        {line.what && helpFor(line.what, sections, rosterSections)
          ? <p class="hint">{helpFor(line.what, sections, rosterSections)}</p>
          : null}

        {replacing ? (
          <small role="status" data-testid="replacing-note">
            Correcting an item already submitted — the new line is saved first, then the old one is deleted.
          </small>
        ) : null}

        {line.what === 'direct_deposit' ? (
          <div class="new-hire-block" data-testid="direct-deposit-details">
            {/* Bank only (owner, 2026-09-24: "no one changed their SSN"). */}
            <RestrictedFields line={line} patch={patch} withSsn={false} />
          </div>
        ) : null}

        {isNewHire && newHireSection ? (
          <div class="new-hire-block" data-testid="new-hire-details">
            <div class="money-add">
              <label class="nh-field">
                <span class="nh-label">First</span>
                <input
                  type="text"
                  value={line.firstName}
                  placeholder="First"
                  aria-label="First"
                  onInput={(e: Event) => patch({ firstName: (e.target as HTMLInputElement).value })}
                />
              </label>
              <label class="nh-field">
                <span class="nh-label">Middle</span>
                <input
                  type="text"
                  value={line.middleName}
                  placeholder="Middle"
                  aria-label="Middle"
                  onInput={(e: Event) => patch({ middleName: (e.target as HTMLInputElement).value })}
                />
              </label>
              <label class="nh-field">
                <span class="nh-label">Last</span>
                <input
                  type="text"
                  value={line.lastName}
                  placeholder="Last"
                  aria-label="Last"
                  onInput={(e: Event) => patch({ lastName: (e.target as HTMLInputElement).value })}
                />
              </label>
            </div>
            <div class="money-add">
              <label class="nh-field">
                <span class="nh-label">{shape.label1}</span>
                <input
                  type={shape.type1}
                  step={shape.type1 === 'number' ? '0.01' : undefined}
                  min={shape.type1 === 'number' ? '0' : undefined}
                  value={line.field1}
                  placeholder={shape.label1}
                  aria-label={shape.label1}
                  onInput={(e: Event) => patch({ field1: (e.target as HTMLInputElement).value })}
                />
              </label>
              <label class="nh-field">
                <span class="nh-label">{shape.label2}</span>
                <input
                  type={shape.type2}
                  step={shape.type2 === 'number' ? '0.01' : undefined}
                  value={line.field2}
                  placeholder={shape.label2}
                  aria-label={shape.label2}
                  onInput={(e: Event) => patch({ field2: (e.target as HTMLInputElement).value })}
                />
              </label>
              <label class="nh-field">
                <span class="nh-label">Note</span>
                <input
                  type="text" value={line.note} placeholder="Note (optional)"
                  aria-label="Note"
                  onInput={(e: Event) => patch({ note: (e.target as HTMLInputElement).value })}
                />
              </label>
            </div>
            <div class="money-add">
              {newHireSection.extraFields.map((f) => (
                <label class="nh-field" key={f.key}>
                  <span class="nh-label">{f.label}</span>
                  <input
                    type={f.type === 'date' ? 'date' : 'text'}
                    value={line.extra[f.key] ?? ''}
                    placeholder={f.label}
                    aria-label={f.label}
                    onInput={(e: Event) => patch({
                      extra: { ...line.extra, [f.key]: (e.target as HTMLInputElement).value },
                    })}
                  />
                </label>
              ))}
            </div>
            <RestrictedFields line={line} patch={patch} />
            <div class="money-add">
              <button
                type="button"
                class="btn primary"
                disabled={busy || !sendable || !activePeriod}
                onClick={() => void submit()}
                data-testid="manager-entry-submit"
              >
                {busy ? 'Saving…' : replacing ? 'Save correction' : 'Submit'}
              </button>
              {replacing ? (
                <button type="button" class="btn" onClick={clearLine} data-testid="cancel-edit">
                  Cancel
                </button>
              ) : null}
            </div>
          </div>
        ) : null}

        <div class="money-add" data-testid="manager-entry-status-row">
          {error ? <small class="bad" role="alert" data-testid="manager-entry-error">{error}</small> : null}
          {done ? <small class="good" role="status" data-testid="manager-entry-done">{done}</small> : null}
        </div>

        {problems.length ? (
          <ul class="bad" data-testid="manager-entry-problems">
            {problems.map((p) => <li key={p}>{p}</li>)}
          </ul>
        ) : null}
        </>
      ) : null}

    </section>

      {/* Three boxes (owner, 2026-09-24): entry above, then the payroll
          export, then what was submitted. */}
      <ManagerEntryReport clinic={clinic} periodId={activePeriod?.id ?? null} />

      <section class="card" data-testid="manager-entry-submitted">
        <PeriodTables
          data={data.data}
          clinic={clinic}
          periods={periods}
          activePeriodId={activePeriod?.id ?? null}
          readOnly={!entryOpen}
          busy={busy}
          onEdit={editLine}
          onEditRoster={editRoster}
          onVoid={(target, id) => void voidItem(target, id)}
          onMove={(target, id, to) => void moveItem(target, id, to)}
        />
      </section>
    </>
  );
}

/** The chosen "what"'s own one-line help text, from the server's list. */
function helpFor(what: string, sections: EntrySection[], rosterSections: RosterSection[]): string {
  return (
    sections.find((s) => s.key === what)?.help
    ?? rosterSections.find((s) => s.key === what)?.help
    ?? ''
  );
}

/** One filed item, money or roster, folded into the shape the unified
 * per-period table renders — so the table does not need to know which of the
 * nine sections a row came from. */
interface UnifiedRow {
  key: string;
  employeeName: string | null;
  employeeId: number | null;
  fileNo: string | null;
  type: string;
  amount: string;
  isDeduction: boolean;
  note: string;
  enteredBy: string;
  enteredAt: string;
  voidedAt: string | null;
  voidedBy: string | null;
  target: VoidTarget;
  id: number;
  /** New hires have no Edit — secure fields are unreadable (SPEC-001 §7).
   * Rate changes do: current rate / new rate can be loaded back. */
  editable: boolean;
  /** N-088: set only on a TERMINATION row. OWNER-only pill (isOwner check in
   * PeriodRowsTable) — a manager sees the row exactly as before. */
  payVerdict: RosterChange['payVerdict'];
  adpCheck: RosterChange['adpCheck'];
  /** Last-4 masks for a new hire / SSN & direct deposit row with details
   * on file; null when there is nothing to show. */
  secureTail: RosterChange['secureTail'];
  /** 097: an owner/director-only bonus line ("Private" badge). Only the
   * owner and director ever receive one, so a manager never sees the badge. */
  private: boolean;
  /** "Completed" (owner, 2026-10-06): a roster item (new hire, termination,
   * rate change, direct deposit, MISC) is work done by hand in ADP, not money
   * on the batch — so it gets a done / not-done mark. Money rows: null. */
  isTodo: boolean;
  completedAt: string | null;
  completedBy: string | null;
}

/** Owner ruling, brief 014, 2026-09-07: "items entered should be showing up
 * below each in their weekly table". `reason` (the section's own detail —
 * "Reason", "What for", ...) and `note` (the free-text box every line carries)
 * are two different fields upstream; the ruling names one Amount/detail
 * column and one Note column, so they are joined here rather than dropped. */
function moneyRow(l: EntryLine, sections: EntrySection[]): UnifiedRow {
  const section = sections.find((s) => s.payCode === l.payCode);
  return {
    key: `earning-${l.id}`,
    employeeName: l.employeeName,
    employeeId: l.employeeId,
    fileNo: l.fileNo,
    type: section?.title ?? l.category,
    amount: l.amount,
    isDeduction: l.kind === 'DEDUCTION',
    note: l.reason && l.note ? `${l.reason} - ${l.note}` : (l.reason || l.note || '—'),
    enteredBy: l.enteredBy,
    enteredAt: l.enteredAt,
    voidedAt: l.voidedAt,
    voidedBy: l.voidedBy,
    target: voidTargetForLine(l),
    id: l.id,
    editable: true,
    payVerdict: null,
    adpCheck: null,
    secureTail: null,
    private: l.private === true,
    isTodo: false,
    completedAt: null,
    completedBy: null,
  };
}

/** Which roster rows get Edit: a rate change (corrected in place), and —
 * N-071 — a termination or a MISC note (replaced: new line in, old voided).
 * New hires and direct deposits stay Delete-only: their SSN / bank fields are
 * never readable back (SPEC-001 §7). Edit needs a person on the row. */
export function rosterEditable(r: Pick<RosterChange, 'changeType' | 'employeeId'>): boolean {
  if (r.changeType === 'RATE_CHANGE') return true;
  return (r.changeType === 'TERMINATION' || r.changeType === 'MISC') && r.employeeId !== null;
}

function rosterRow(r: RosterChange): UnifiedRow {
  return {
    key: `roster-${r.id}`,
    employeeName: r.employeeName,
    employeeId: r.employeeId,
    fileNo: r.fileNo,
    type: ROSTER_CHANGE_LABELS[r.changeType],
    // No amount on a roster row - the hire/term/rate DETAIL fills the same
    // column instead (owner ruling, brief 014).
    amount: r.changeType === 'DIRECT_DEPOSIT' ? '—' : `${r.field1 || '—'} / ${r.field2 || '—'}`,
    isDeduction: false,
    note: r.note || '—',
    enteredBy: r.enteredBy,
    enteredAt: r.enteredAt,
    voidedAt: r.voidedAt,
    voidedBy: r.voidedBy,
    target: 'roster',
    id: r.id,
    editable: rosterEditable(r),
    payVerdict: r.payVerdict,
    adpCheck: r.adpCheck ?? null,
    secureTail: !r.voidedAt ? (r.secureTail ?? null) : null,
    private: false,
    isTodo: true,
    completedAt: r.enteredInAdpAt ?? null,
    completedBy: r.enteredInAdpBy ?? null,
  };
}

/** The done / not-done mark on a roster item. Owner and director toggle it
 * (the server's own gate, 078); everyone else reads it. Reversible. */
function CompletedControl({ row }: { row: UnifiedRow }): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const role = session.value.me?.role;
  const canMark = (role === 'OWNER' || role === 'DIRECTOR') && !session.value.me?.viewingAs;
  const done = Boolean(row.completedAt);
  if (!canMark || row.voidedAt) {
    return done
      ? <span class="badge ok" data-testid={`completed-${row.id}`} title={`Completed by ${row.completedBy ?? ''}`}>Completed</span>
      : <span class="badge warn" data-testid={`not-done-${row.id}`}>Not done</span>;
  }
  async function toggle(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await setRosterEnteredInAdp(row.id, !done);
      await refetchResource(MANAGER_ENTRY_KEY, () => loadManagerEntry(null));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String((e as Error).message ?? e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <button
        type="button"
        class={done ? 'btn' : 'btn primary'}
        disabled={busy}
        data-testid={`completed-toggle-${row.id}`}
        title={done ? `Completed by ${row.completedBy ?? ''} ${row.completedAt ? formatDate(row.completedAt) : ''} — click to undo` : 'Mark this as done in ADP'}
        onClick={() => void toggle()}
      >
        {busy ? 'Saving…' : done ? 'Completed' : 'Mark completed'}
      </button>
      {error ? <small class="bad" role="alert">{error}</small> : null}
    </>
  );
}

/** Every item filed for one clinic and one pay period, money and roster
 * together, in the single table the owner asked for. Scoped through
 * `manager_entry_submission` - the same location scoping `SubmittedSoFar`
 * used before this redesign - never through the line's own employee record,
 * which can belong to a different home clinic than the submission. */
function rowsForPeriod(
  data: ManagerEntryData, clinic: LocationCode, periodId: number, sections: EntrySection[],
): UnifiedRow[] {
  const subs = data.submissions.filter((s) => s.locationCode === clinic && s.payPeriodId === periodId);
  const ids = new Set(subs.map((s) => s.id));
  const lines = data.lines.filter((l) => ids.has(l.submissionId)).map((l) => moneyRow(l, sections));
  const roster = data.rosterChanges.filter((r) => ids.has(r.submissionId)).map(rosterRow);
  return [...lines, ...roster];
}

/**
 * Every pay period, each as its own table (owner ruling, brief 014,
 * 2026-09-07): the currently selected period first, open for entry and
 * expanded exactly like before this redesign; every other period below it,
 * newest first, collapsed behind a native `<details>` so the page does not
 * grow one screen-length per week. A past period stays read-only (L-15) -
 * only the selected period, and only while it is still open for entry, gets
 * Edit/Delete.
 */
function PeriodTables({
  data, clinic, periods, activePeriodId, readOnly, busy, onEdit, onEditRoster, onVoid, onMove,
}: {
  data: ManagerEntryData;
  clinic: LocationCode;
  periods: EntryPayPeriod[];
  activePeriodId: number | null;
  /** Whether the SELECTED period may still be edited (L-15). Every other
   * period is read-only regardless - it is not the one being filed against. */
  readOnly: boolean;
  busy: boolean;
  onEdit: (line: EntryLine) => void;
  onEditRoster: (row: RosterChange) => void;
  onVoid: (target: VoidTarget, id: number) => void;
  onMove: (target: VoidTarget, id: number, targetPayPeriodId: number) => void;
}): JSX.Element {
  const active = periods.find((p) => p.id === activePeriodId) ?? null;
  const others = periods
    .filter((p) => p.id !== activePeriodId)
    .sort((a, b) => b.startDate.localeCompare(a.startDate));

  function edit(row: UnifiedRow): void {
    if (row.target === 'roster') {
      const change = data.rosterChanges.find((r) => r.id === row.id);
      if (change) onEditRoster(change);
      return;
    }
    const line = data.lines.find((l) => l.id === row.id && voidTargetForLine(l) === row.target);
    if (line) onEdit(line);
  }

  return (
    <>
      {active ? (
        <PeriodTable
          period={active}
          clinic={clinic}
          rows={rowsForPeriod(data, clinic, active.id, data.sections)}
          collapsed={false}
          readOnly={readOnly}
          busy={busy}
          onEdit={edit}
          onVoid={onVoid}
          onMove={onMove}
          openPeriods={periods.filter((x) => !x.closed && x.id !== active.id)}
        />
      ) : null}
      {others.map((p) => (
        <PeriodTable
          key={p.id}
          period={p}
          clinic={clinic}
          rows={rowsForPeriod(data, clinic, p.id, data.sections)}
          collapsed
          readOnly
          busy={busy}
          onEdit={edit}
          onVoid={onVoid}
          onMove={onMove}
          openPeriods={periods.filter((x) => !x.closed && x.id !== p.id)}
        />
      ))}
    </>
  );
}

/** One pay period's table. The open (selected) period renders plain, under
 * "Submitted this period", matching what this page looked like before this
 * redesign; every earlier or later period renders inside a `<details>` whose
 * summary names the week, its dates, and its item count - never a bare
 * "Week N" (R-10; `payPeriodLabel` already carries the dates). */
function PeriodTable({ period, clinic, rows, collapsed, readOnly, busy, onEdit, onVoid, onMove, openPeriods }: {
  period: EntryPayPeriod;
  clinic: LocationCode;
  rows: UnifiedRow[];
  collapsed: boolean;
  readOnly: boolean;
  busy: boolean;
  onEdit: (row: UnifiedRow) => void;
  onVoid: (target: VoidTarget, id: number) => void;
  onMove: (target: VoidTarget, id: number, targetPayPeriodId: number) => void;
  openPeriods: EntryPayPeriod[];
}): JSX.Element {
  const table = <PeriodRowsTable rows={rows} readOnly={readOnly} busy={busy} onEdit={onEdit} onVoid={onVoid} onMove={onMove} movePeriods={period.closed ? [] : openPeriods} clinic={clinic} periodId={period.id} />;
  const summary = `${payPeriodLabel(period, clinic).replace(/ .. closed$/, '')} · ${rows.length} item${rows.length === 1 ? '' : 's'}`;

  if (!collapsed) {
    return (
      <div data-testid={`period-table-${period.id}`}>
        <div class="section-head">
          <h3>Submitted this period</h3>
          <p>
            {rows.filter((r) => r.voidedAt === null).length} live item(s).
            {(() => {
              const todo = rows.filter((r) => r.voidedAt === null && r.isTodo);
              if (!todo.length) return null;
              const done = todo.filter((r) => r.completedAt).length;
              return <> <span class={done === todo.length ? 'badge ok' : 'badge warn'} data-testid="completed-count">{`${done} of ${todo.length} non-payroll item(s) completed`}</span></>;
            })()}
          </p>
        </div>
        {table}
      </div>
    );
  }

  return (
    <details data-testid={`period-table-${period.id}`}>
      <summary>{summary}</summary>
      {table}
    </details>
  );
}

/** The actual rows, sortable by heading (owner, 2026-08-19). Split out so the
 * sort hook has a component to live in - a hook cannot run inside a `.map()`
 * callback, and every period gets its own instance of this table. */
function PeriodRowsTable({ rows: allRows, readOnly, busy, onEdit, onVoid, onMove, movePeriods = [], clinic, periodId, includeVoided = false }: {
  rows: UnifiedRow[];
  readOnly: boolean;
  busy: boolean;
  onEdit: (row: UnifiedRow) => void;
  onVoid: (target: VoidTarget, id: number) => void;
  onMove?: (target: VoidTarget, id: number, targetPayPeriodId: number) => void;
  /** Open weeks this line could move to (idea 34); empty hides the control. */
  movePeriods?: EntryPayPeriod[];
  clinic?: LocationCode;
  /** The week these rows belong to: a roster name opens its record in the export box. */
  periodId?: number;
  includeVoided?: boolean;
}): JSX.Element {
  // N-088: the ADP verdict pill is OWNER only (Request Ledger, 2026-09-08) —
  // a manager sees the roster row exactly as before this change.
  const isOwner = session.value.me?.role === 'OWNER';
  const canMove = (isOwner || session.value.me?.role === 'DIRECTOR') && !!onMove && movePeriods.length > 0;
  // Tomer, 2026-09-07: voided items (smoke tests, mistakes) are never deleted —
  // the trail stays — but they do not belong in the working table. Live items
  // only; the voided ones fold away under a "voided (N)" toggle.
  const rows = includeVoided ? allRows : allRows.filter((r) => r.voidedAt === null);
  const voided = includeVoided ? [] : allRows.filter((r) => r.voidedAt !== null);
  const sorted = useTableSort(rows, {
    employee: (r) => r.employeeName,
    type: (r) => r.type,
    amount: (r) => (r.isDeduction ? -1 : 1) * (Number.isNaN(Number(r.amount)) ? 0 : Number(r.amount)),
    note: (r) => r.note,
    by: (r) => r.enteredBy,
    at: (r) => r.enteredAt,
    done: (r) => (r.isTodo ? (r.completedAt ? 2 : 1) : 0),
  });

  const voidedToggle = voided.length ? (
    <details class="voided-items">
      <summary>{`voided (${voided.length})`}</summary>
      <PeriodRowsTable rows={voided} readOnly={readOnly} busy={busy} onEdit={onEdit} onVoid={onVoid} includeVoided />
    </details>
  ) : null;

  if (!rows.length) {
    return <div><p>Nothing filed for this clinic and period yet.</p>{voidedToggle}</div>;
  }

  return (
    <div class="table-wrap">
      {voidedToggle}
      <table>
        <thead>
          <tr>
            <SortableTh label="Employee" sortKey="employee" state={sorted.state} onSort={sorted.toggle} />
            <SortableTh label="Type" sortKey="type" state={sorted.state} onSort={sorted.toggle} />
            <SortableTh label="Amount" sortKey="amount" state={sorted.state} onSort={sorted.toggle} className="num" />
            <SortableTh label="Note" sortKey="note" state={sorted.state} onSort={sorted.toggle} />
            <SortableTh label="Entered by" sortKey="by" state={sorted.state} onSort={sorted.toggle} />
            <SortableTh label="Entered at" sortKey="at" state={sorted.state} onSort={sorted.toggle} />
            <SortableTh label="Done" sortKey="done" state={sorted.state} onSort={sorted.toggle} />
            <th />
          </tr>
        </thead>
        <tbody>
          {sorted.rows.map((r) => {
            const who = r.employeeName ?? (r.employeeId ? `Employee #${r.employeeId}` : 'New hire');
            return (
              <tr key={r.key} class={r.voidedAt ? 'voided' : undefined}>
                <td>
                  {r.target === 'roster' && periodId != null ? (
                    <button
                      type="button" class="link"
                      data-testid={`submitted-roster-${r.id}-open`}
                      onClick={() => { rosterDetailRequest.value = { payPeriodId: periodId, rosterId: r.id }; }}
                    >
                      {who}
                    </button>
                  ) : who}
                  {r.fileNo ? <span class="role-chip"> #{r.fileNo}</span> : null}
                </td>
                <td>
                  {r.type}
                  {r.private ? <>{' '}<span class="badge" data-testid="private-badge">Private</span></> : null}
                  {isOwner ? <>{' '}<PayVerdictPill verdict={r.payVerdict} />{' '}<AdpCheckPill check={r.adpCheck} /></> : null}
                  {r.secureTail ? <ShowSecure rosterChangeId={r.id} tail={r.secureTail} withSsn={r.type !== ROSTER_CHANGE_LABELS.DIRECT_DEPOSIT} /> : null}
                </td>
                <td class="num">{r.isDeduction ? `(${r.amount})` : r.amount}</td>
                <td>{r.note}</td>
                <td>{r.enteredBy}</td>
                <td>{formatDate(r.enteredAt)}</td>
                <td>{r.isTodo ? <CompletedControl row={r} /> : <span class="muted" title="Goes on the payroll batch">Payroll</span>}</td>
                <td>
                  {!r.voidedAt && canMove && r.target !== 'roster' ? (
                    <select
                      class="btn" disabled={busy} value=""
                      data-testid={`move-line-${r.id}`}
                      aria-label={`Move ${r.type} for ${who} to another week`}
                      onChange={(e: Event) => {
                        const v = Number((e.target as HTMLSelectElement).value);
                        if (v) onMove!(r.target, r.id, v);
                      }}
                    >
                      <option value="">Move to week…</option>
                      {movePeriods.map((p) => <option key={p.id} value={p.id}>{payPeriodLabel(p, clinic)}</option>)}
                    </select>
                  ) : null}
                  {r.voidedAt ? (
                    <span class="role-chip">{`voided by ${r.voidedBy ?? 'unknown'} at ${formatDate(r.voidedAt)}`}</span>
                  ) : readOnly ? null : (
                    <>
                      {r.editable ? (
                        <button
                          type="button" class="btn" disabled={busy}
                          aria-label={`Edit ${r.type} for ${who}`}
                          onClick={() => onEdit(r)}
                        >
                          Edit
                        </button>
                      ) : null}
                      <button
                        type="button" class="btn" disabled={busy}
                        aria-label={`Delete ${r.type} for ${who}`}
                        onClick={() => onVoid(r.target, r.id)}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Whether this viewer gets the entry form at all. Everyone mapped does: a
 * manager for their own clinic (the live app's Payroll Extras page is
 * "full read-write on own location only", with no read-only variant), an
 * admin for any. The server decides for real (INV-3). */
export function canEnter(): boolean {
  return session.value.me?.mapped === true;
}
